import { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { toast } from 'sonner';
import {
  X, Sparkles, Crown, Globe2, Gauge, ShieldAlert, Brain, Eye, Layers, Radar,
} from 'lucide-react';

/**
 * PlusUpsellModal — Round 104g (Jun 2026). The DiviDrip Plus sales
 * pitch shown when a free user clicks a gold lock (Dashboard/Screener
 * 3-way switch, Squeeze Radar chip). Owner picked variant A (grouped
 * feature grid); B/C prototypes and the preview switcher were removed.
 *
 * Layout: two explicit columns (not a row-aligned grid) so sections
 * stack tightly — left: Full Market Access → Forensic & Risk →
 * Ownership Intelligence; right: Quant & Valuation → AI Research →
 * Power Tools. Phone: full-screen with internal scroll.
 */

const LEFT_SECTIONS = [
  {
    icon: Globe2, tone: 'text-sky-500', title: 'Full Market Access',
    features: [
      ['All + Non-Dividend views', '4,000+ non-dividend stocks join the 5,300 dividend payers — Dashboard, Screener & Top Movers'],
      ['Non-Dividend Stock Screener', 'every filter, preset and strategy applied to the full universe'],
      ['Top 10 Daily Non-Dividend Movers', 'real-time leaderboard'],
      ['Full-universe Squeeze Radar', 'short-squeeze candidates beyond dividend payers'],
      ['IPO Watch — full universe', 'every new listing incl. SPACs, not just dividend IPOs'],
      ['Founder-Led: Non-Dividend & All views', '420+ non-dividend founder-led compounders join the free dividend list'],
      ['Peer & ETF-holding drill-through', 'open locked non-dividend peer chips and fund holdings straight into the modal'],
    ],
  },
  {
    icon: ShieldAlert, tone: 'text-rose-500', title: 'Forensic & Risk',
    features: [
      ['Altman Z-score zones', 'Safe / Grey / Distress bankruptcy risk'],
      ['Beneish M-score flags', 'earnings-manipulation screen'],
      ['Biotech R&D capitalization', 'zero-revenue names scored correctly'],
      ['10-K / 8-K Risk Factors', 'SEC EDGAR filings with a 3-level risk taxonomy'],
    ],
  },
  {
    icon: Eye, tone: 'text-amber-500', title: 'Ownership Intelligence',
    features: [
      ['Insider Trades — Form 4 + Form 3', 'who is buying, who is selling, including initial stakes'],
      ['Institutional Holders (13F)', 'fund positions and quarterly changes'],
    ],
  },
];

const RIGHT_SECTIONS = [
  {
    icon: Gauge, tone: 'text-emerald-500', title: 'Quant & Valuation',
    features: [
      ['Capital Quality Rank (1–100)', 'sector-neutral 4-factor composite: Shareholder Yield · CFROI · Momentum · Tail-Risk'],
      ['FCFE & Multiplier Valuation Matrix', 'two-stage fair value with a Target Buy Price'],
      ['Capital Reinvestment Triangle', '3-spoke capital-quality radar'],
      ['Buyback yield tracking', 'true net float change, not gross announcements'],
      ['Dual-class normalization', 'BRK.A/B, GOOG/GOOGL treated correctly'],
    ],
  },
  {
    icon: Radar, tone: 'text-fuchsia-500', title: 'Coverage Radars',
    features: [
      ['Cut-Risk Radar (Coverage Watch)', 'SEC-reported FCF / FFO / NII coverage below 1.0\u00d7 and deteriorating — catch cuts before they\u2019re announced'],
      ['Recovery Radar (Coverage Improving)', 'coverage above 1.0\u00d7 and rising fast — the setup before a raise'],
      ['Watchlist radar cards + Coverage column', 'your names checked automatically on the Dashboard, sortable coverage in the Screener'],
    ],
  },
  {
    icon: Brain, tone: 'text-violet-500', title: 'AI Research',
    features: [
      ['AI Capital Analytics Thesis', 'buy / hold / avoid memo generated per ticker'],
      ['Capital Quality Leaderboard', 'top non-dividend names, refreshed weekly'],
    ],
  },
  {
    icon: Layers, tone: 'text-cyan-500', title: 'Power Tools',
    features: [
      ['5 watchlists · 50 stocks each', 'your original watchlist (10-stock limit on Free) upgrades to 50 too'],
      ['5 portfolios · 100 positions each', 'track every account separately — original portfolio upgraded as well'],
    ],
  },
];

function Section({ sec }) {
  return (
    <div>
      <div className="flex items-center gap-2 mb-2 sm:mb-1.5">
        <sec.icon className={`w-4 h-4 ${sec.tone}`} />
        <h3 className="text-sm sm:text-[13px] font-bold uppercase tracking-wider">{sec.title}</h3>
      </div>
      <ul className="space-y-2 sm:space-y-1.5">
        {sec.features.map(([name, desc]) => (
          <li key={name} className="flex gap-2 items-start">
            <Sparkles className="w-3.5 h-3.5 text-yellow-500 dark:text-yellow-400 shrink-0 mt-0.5" />
            <div className="min-w-0">
              <p className="text-sm sm:text-[13px] font-semibold leading-snug">{name}</p>
              <p className="text-[11px] sm:text-[10px] text-muted-foreground leading-snug">{desc}</p>
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}

export default function PlusUpsellModal({ open, onClose }) {
  useEffect(() => {
    if (!open) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKey = (e) => { if (e.key === 'Escape') onClose?.(); };
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener('keydown', onKey);
    };
  }, [open, onClose]);

  if (!open) return null;

  const modal = (
    <div
      className="fixed inset-0 z-[100] flex items-stretch sm:items-center justify-center sm:p-3"
      role="dialog"
      aria-modal="true"
      data-testid="plus-upsell-modal"
    >
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose} data-testid="plus-upsell-backdrop" />
      <div className="relative w-full sm:max-w-5xl h-full sm:h-auto sm:max-h-[97vh] sm:rounded-2xl border-0 sm:border border-yellow-500/40 bg-card shadow-2xl overflow-hidden flex flex-col">
        <button
          type="button"
          onClick={onClose}
          data-testid="plus-upsell-close"
          className="absolute right-3 top-3 z-10 inline-flex items-center justify-center w-8 h-8 rounded-full bg-card/80 text-muted-foreground hover:text-foreground hover:bg-muted/60 transition-colors"
          aria-label="Close"
        >
          <X className="w-4 h-4" />
        </button>

        <div className="overflow-y-auto flex-1 min-h-0">
          {/* Hero */}
          <div className="relative px-5 sm:px-7 pt-6 pb-4 border-b border-border bg-gradient-to-r from-yellow-500/10 via-amber-500/5 to-transparent">
            <div className="flex items-center gap-2 mb-1">
              <Crown className="w-4 h-4 text-yellow-500 dark:text-yellow-400" />
              <span className="text-[11px] font-bold uppercase tracking-[0.18em] text-yellow-700 dark:text-yellow-400">DiviDrip Plus</span>
            </div>
            <h2 className="text-3xl sm:text-4xl font-black tracking-tight bg-gradient-to-r from-yellow-500 via-amber-500 to-yellow-600 bg-clip-text text-transparent" data-testid="plus-upsell-headline">
              UNLOCK THE WHOLE MARKET
            </h2>
            <p className="mt-1.5 text-sm text-muted-foreground max-w-2xl">
              Institutional-grade analytics across the full U.S. equity universe — quant models,
              forensic screens, AI research and SEC filings intelligence, built for long-term compounding.
            </p>
            <div className="mt-3 flex flex-wrap gap-1.5">
              {['9,300+ stocks', '30+ pro analytics', 'AI research memos', 'SEC filings intelligence'].map((s) => (
                <span key={s} className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-amber-500/15 text-amber-700 dark:text-amber-400 ring-1 ring-amber-500/30">{s}</span>
              ))}
            </div>
          </div>

          {/* Two tight columns — no row-aligned grid gaps */}
          <div className="px-5 sm:px-7 py-5 sm:py-4 grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-6 sm:gap-y-5 items-start">
            <div className="space-y-6 sm:space-y-5">
              {LEFT_SECTIONS.map((sec) => <Section key={sec.title} sec={sec} />)}
            </div>
            <div className="space-y-6 sm:space-y-5">
              {RIGHT_SECTIONS.map((sec) => <Section key={sec.title} sec={sec} />)}
            </div>
          </div>
        </div>

        {/* CTA footer */}
        <div className="px-5 sm:px-7 py-4 border-t border-border bg-muted/30 flex flex-col sm:flex-row items-center justify-between gap-3 shrink-0">
          <p className="text-xs text-muted-foreground text-center sm:text-left">
            Everything you use today stays free forever. Plus adds the rest of the market.
          </p>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={onClose}
              data-testid="plus-upsell-dismiss"
              className="px-4 py-2 text-xs font-semibold rounded-full text-muted-foreground hover:text-foreground hover:bg-muted/60 transition-colors"
            >
              Maybe later
            </button>
            <button
              type="button"
              onClick={() => {
                toast('DiviDrip Plus is launching soon', {
                  description: 'Pricing goes live shortly — everything you saw here unlocks the moment you upgrade.',
                  duration: 5000,
                });
              }}
              data-testid="plus-upsell-cta"
              className="inline-flex items-center gap-1.5 px-5 py-2 text-xs font-bold rounded-full bg-gradient-to-r from-yellow-500 to-amber-500 hover:from-yellow-400 hover:to-amber-400 text-black shadow-lg shadow-amber-500/20 transition-all"
            >
              <Crown className="w-3.5 h-3.5" />
              Get DiviDrip Plus
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  return createPortal(modal, document.body);
}
